'use client'

import { useState, useEffect } from 'react'
import { useReadContract, useBlockNumber, useAccount, useWriteContract, useWaitForTransactionReceipt, useChainId } from 'wagmi'
import { formatEther, parseEther } from 'viem'
import { useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { MULTISTAKING_CONTRACT_ADDRESS, MULTISTAKING_ABI, STAKE_TOKEN_ADDRESS, ERC20_ABI } from '@/lib/contracts'
import { TransactionHash } from '@/components/ui/transaction-hash'
import { triggerWalletReminder } from '@/components/wallet-reminder-bot'
import { toast } from 'sonner'
import { blocksToTime, getBlockTimeSeconds } from '@/lib/block-times'

export function PoolList() {
  const chainId = useChainId()
  const [expandedSections, setExpandedSections] = useState({
    open: true,
    running: true,
    finished: false
  })

  // Get total number of pools
  const { data: poolLength, queryKey: poolLengthQueryKey } = useReadContract({
    address: MULTISTAKING_CONTRACT_ADDRESS,
    abi: MULTISTAKING_ABI,
    functionName: 'poolLength',
    scopeKey: 'pools',
  })

  // Get current block number for status calculation
  const { data: currentBlock } = useBlockNumber({
    watch: true,
  })

  // Generate array of pool IDs based on pool length
  const poolIds = poolLength ? Array.from({ length: Number(poolLength) }, (_, i) => i) : []

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const SectionHeader = ({ 
    title, 
    count, 
    emoji, 
    bgColor, 
    textColor,
    section,
    description 
  }: { 
    title: string
    count: number
    emoji: string
    bgColor: string
    textColor: string
    section: keyof typeof expandedSections
    description: string
  }) => (
    <div 
      className={`${bgColor} border border-green-700/30 rounded-lg p-4 cursor-pointer hover:border-green-600/50 transition-all`}
      onClick={() => toggleSection(section)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{emoji}</span>
          <div>
            <h3 className={`font-mono font-bold text-lg ${textColor}`}>
              {title} Pools
            </h3>
            <p className="text-sm text-green-700/80 font-mono">
              {description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={`border-green-700/50 ${textColor} font-mono font-bold`}>
            {count} pool{count !== 1 ? 's' : ''}
          </Badge>
          <div className={`transform transition-transform ${expandedSections[section] ? 'rotate-180' : ''}`}>
            <span className="text-green-600 font-mono">▼</span>
          </div>
        </div>
      </div>
    </div>
  )

  // Categorize pools by fetching details inside PoolCard components
  const categorizePool = (poolId: number, poolDetails: any) => {
    if (!currentBlock || !poolDetails) return 'unknown'

    const current = Number(currentBlock)
    const start = Number(poolDetails[1])
    const end = Number(poolDetails[2])

    if (current < start) {
      return 'open'
    } else if (current >= start && current <= end) {
      return 'running'
    } else {
      return 'finished'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-mono text-green-600">🎱 Staking Pools</h2>
          <p className="text-sm text-green-700/80 font-mono">
            Browse and interact with pools organized by status
          </p>
        </div>
        {currentBlock && (
          <div className="text-xs text-green-600/70 font-mono">
            Block: <span className="text-green-500">{currentBlock.toString()}</span>
          </div>
        )}
      </div>

      {poolIds.length === 0 ? (
        <div className="bg-black border border-green-700/30 rounded-lg p-8">
          <div className="text-center text-green-700/80 font-mono">
            <p>No pools found or still loading...</p>
            <p className="text-sm mt-2">
              Total pools: {poolLength ? poolLength.toString() : 'Loading...'}
            </p>
          </div>
        </div>
      ) : (
        <PoolListSections
          poolIds={poolIds}
          currentBlock={currentBlock}
          chainId={chainId}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          SectionHeader={SectionHeader}
        />
      )}
    </div>
  )
}

// Separate component to handle the pool sections with proper hook usage
function PoolListSections({
  poolIds,
  currentBlock,
  chainId,
  expandedSections,
  toggleSection,
  SectionHeader
}: {
  poolIds: number[]
  currentBlock?: bigint
  chainId?: number
  expandedSections: any
  toggleSection: any
  SectionHeader: any
}) {
  // Pre-fetch all pool details with individual hooks (up to reasonable limit)
  const maxPools = 50 // Reasonable limit to prevent too many hooks
  const poolDetailsHooks = Array.from({ length: maxPools }, (_, i) => {
    const shouldFetch = i < poolIds.length
    return useReadContract({
      address: MULTISTAKING_CONTRACT_ADDRESS,
      abi: MULTISTAKING_ABI,
      functionName: 'getPoolDetails',
      args: shouldFetch ? [BigInt(i)] : undefined,
      scopeKey: 'pools',
      query: {
        enabled: shouldFetch
      }
    })
  })

  // Categorize pools based on fetched details
  const categorizedPools = {
    open: [] as number[],
    running: [] as number[],
    finished: [] as number[]
  }

  poolIds.forEach((poolId, index) => {
    const poolDetails = poolDetailsHooks[index]?.data
    if (!currentBlock || !poolDetails) return

    const current = Number(currentBlock)
    const start = Number(poolDetails[1])
    const end = Number(poolDetails[2])

    if (current < start) {
      categorizedPools.open.push(poolId)
    } else if (current >= start && current <= end) {
      categorizedPools.running.push(poolId)
    } else {
      categorizedPools.finished.push(poolId)
    }
  })

  return (
    <div className="space-y-4">
      {/* Open Pools Section */}
      <div className="space-y-3">
        <SectionHeader
          title="Open"
          count={categorizedPools.open.length}
          emoji="🟢"
          bgColor="bg-green-900/10"
          textColor="text-green-400"
          section="open"
          description="Available for staking - join before they start!"
        />
        
        {expandedSections.open && (
          <div className="space-y-3 ml-4">
            {categorizedPools.open.length > 0 ? (
              categorizedPools.open.map(poolId => (
                <PoolCard
                  key={poolId}
                  poolId={poolId}
                  currentBlock={currentBlock}
                  chainId={chainId}
                />
              ))
            ) : (
              <div className="bg-green-900/5 border border-green-700/20 rounded-lg p-4">
                <p className="text-green-700/80 font-mono text-sm text-center">
                  No open pools available for staking
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Running Pools Section */}
      <div className="space-y-3">
        <SectionHeader
          title="Running"
          count={categorizedPools.running.length}
          emoji="🟡"
          bgColor="bg-yellow-900/10"
          textColor="text-yellow-400"
          section="running"
          description="Currently earning rewards - watch the progress!"
        />
        
        {expandedSections.running && (
          <div className="space-y-3 ml-4">
            {categorizedPools.running.length > 0 ? (
              categorizedPools.running.map(poolId => (
                <PoolCard
                  key={poolId}
                  poolId={poolId}
                  currentBlock={currentBlock}
                  chainId={chainId}
                />
              ))
            ) : (
              <div className="bg-yellow-900/5 border border-yellow-700/20 rounded-lg p-4">
                <p className="text-yellow-700/80 font-mono text-sm text-center">
                  No pools currently running
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Finished Pools Section */}
      <div className="space-y-3">
        <SectionHeader
          title="Finished"
          count={categorizedPools.finished.length}
          emoji="🔵"
          bgColor="bg-blue-900/10"
          textColor="text-blue-400"
          section="finished"
          description="Completed pools - withdraw your rewards!"
        />
        
        {expandedSections.finished && (
          <div className="space-y-3 ml-4">
            {categorizedPools.finished.length > 0 ? (
              categorizedPools.finished.map(poolId => (
                <PoolCard
                  key={poolId}
                  poolId={poolId}
                  currentBlock={currentBlock}
                  chainId={chainId}
                />
              ))
            ) : (
              <div className="bg-blue-900/5 border border-blue-700/20 rounded-lg p-4">
                <p className="text-blue-700/80 font-mono text-sm text-center">
                  No finished pools available
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface PoolCardProps {
  poolId: number
  currentBlock?: bigint
  chainId?: number
}

function PoolCard({ poolId, currentBlock, chainId }: PoolCardProps) {
  const { address } = useAccount()
  
  // Fetch pool details
  const { data: poolDetails, isLoading } = useReadContract({
    address: MULTISTAKING_CONTRACT_ADDRESS,
    abi: MULTISTAKING_ABI,
    functionName: 'getPoolDetails',
    args: [BigInt(poolId)],
    scopeKey: 'pools',
  })


  // Get user's stakes for all pools
  const { data: userStakes } = useReadContract({
    address: MULTISTAKING_CONTRACT_ADDRESS,
    abi: MULTISTAKING_ABI,
    functionName: 'getUserStakes',
    args: address ? [address] : undefined,
    scopeKey: 'stakes',
    query: {
      enabled: !!address,
    },
  })

  console.log("User id", poolId, userStakes)

  if (isLoading) {
    return (
      <div className="bg-black border border-green-700/30 rounded-lg p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-green-700/20 rounded w-32"></div>
          <div className="h-5 bg-green-700/20 rounded w-20"></div>
        </div>
        <div className="grid grid-cols-4 gap-6">
          <div className="h-12 bg-green-700/20 rounded"></div>
          <div className="h-12 bg-green-700/20 rounded"></div>
          <div className="h-12 bg-green-700/20 rounded"></div>
          <div className="h-12 bg-green-700/20 rounded"></div>
        </div>
      </div>
    )
  }

  if (!poolDetails) {
    return (
      <div className="bg-black border border-red-700/50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <span className="font-mono text-green-600 text-lg">Pool #{poolId.toString().padStart(2, '0')}</span>
          <span className="text-red-400 text-sm font-mono">Failed to load</span>
        </div>
      </div>
    )
  }

  const [totalStake, startBlock, endBlock, rewardMultiplier, rewardsClaimed] = poolDetails

  // Calculate pool status based on current block
  const getPoolStatus = () => {
    if (!currentBlock) return { status: 'unknown', color: 'text-gray-500', bgColor: 'bg-gray-900/20', description: 'Loading...' }

    const current = Number(currentBlock)
    const start = Number(startBlock)
    const end = Number(endBlock)

    if (current < start) {
      const blocksUntilStart = start - current
      const timeUntilStart = blocksToTime(blocksUntilStart, chainId)
      return {
        status: 'open',
        color: 'text-green-400',
        bgColor: 'bg-green-900/20',
        description: `Opens in ${blocksUntilStart} blocks (~${timeUntilStart.formatted})`
      }
    } else if (current >= start && current <= end) {
      const blocksRemaining = end - current
      const timeRemaining = blocksToTime(blocksRemaining, chainId)
      return {
        status: 'running',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-900/20',
        description: `Ends in ${blocksRemaining} blocks (~${timeRemaining.formatted})`
      }
    } else {
      const blocksSinceEnd = current - end
      const timeSinceEnd = blocksToTime(blocksSinceEnd, chainId)
      return {
        status: 'finished',
        color: 'text-blue-400',
        bgColor: 'bg-blue-900/20',
        description: `Ended ${blocksSinceEnd} blocks ago (~${timeSinceEnd.formatted} ago)`
      }
    }
  }

  const poolStatus = getPoolStatus()
  const rewardMultiplierFormatted = (Number(rewardMultiplier) / 10000).toFixed(2)

  // Check if user has stake in this pool
  const hasStake = userStakes && poolId < userStakes[0].length && 
                   Number(userStakes[0][poolId]) > 0 && 
                   !userStakes[2][poolId]

  const userStakeAmount = userStakes && poolId < userStakes[0].length ? 
                         formatEther(userStakes[0][poolId]) : '0'

  return (
    <div className={`border border-green-700/30 rounded-lg p-6 hover:border-green-600/50 transition-all group ${poolStatus.bgColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <span className="font-mono text-green-600 text-xl font-medium">
            Pool #{poolId.toString().padStart(2, '0')}
          </span>
          <Badge variant="outline" className={`border-green-700/50 text-xs font-mono capitalize ${poolStatus.color}`}>
            {poolStatus.status}
          </Badge>
        </div>
        <div className="text-right">
          <div className="text-green-500 font-mono text-lg font-bold">{rewardMultiplierFormatted}x</div>
          <div className="text-green-600/60 text-xs font-mono uppercase">Rewards</div>
        </div>
      </div>

      {/* Pool Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div>
          <div className="text-green-600/60 text-xs font-mono uppercase mb-1">Total Staked</div>
          <div className="text-green-500 font-mono font-bold">{formatEther(totalStake)}</div>
          <div className="text-green-600/40 text-xs font-mono">AMIRA</div>
        </div>

        <div>
          <div className="text-green-600/60 text-xs font-mono uppercase mb-1">Rewards Claimed</div>
          <div className="text-green-500 font-mono font-bold">{formatEther(rewardsClaimed)}</div>
          <div className="text-green-600/40 text-xs font-mono">AMIRA</div>
        </div>

        <div>
          <div className="text-green-600/60 text-xs font-mono uppercase mb-1">Duration</div>
          <div className="text-green-500 font-mono text-sm">
            {(() => {
              const duration = Number(endBlock) - Number(startBlock)
              const time = blocksToTime(duration, chainId)
              return time.formatted
            })()}
          </div>
          <div className="text-green-600/40 text-xs font-mono">{Number(endBlock) - Number(startBlock)} blocks</div>
        </div>

        <div>
          <div className="text-green-600/60 text-xs font-mono uppercase mb-1">Start Block</div>
          <div className="text-green-500 font-mono text-sm">{startBlock.toString()}</div>
        </div>

        <div>
          <div className="text-green-600/60 text-xs font-mono uppercase mb-1">End Block</div>
          <div className="text-green-500 font-mono text-sm">{endBlock.toString()}</div>
        </div>
      </div>

      {/* User's Personal Information */}
      {userStakes && address && (
        <div className="mb-4 p-4 bg-blue-900/10 border border-blue-700/20 rounded-lg">
          <h4 className="text-blue-400 font-mono text-sm mb-3">Your Position</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-blue-600/70 text-xs font-mono uppercase mb-1">Your Stake</div>
              <div className={`font-mono font-bold ${hasStake ? 'text-blue-400' : 'text-gray-500'}`}>
                {userStakeAmount}
              </div>
              <div className="text-blue-600/40 text-xs font-mono">AMIRA</div>
            </div>
            <div>
              <div className="text-green-600/70 text-xs font-mono uppercase mb-1">Your Claimable</div>
              <div className="text-green-400 font-mono font-bold">
                {userStakes && poolId < userStakes[1].length ? 
                  formatEther(userStakes[1][poolId]) : '0'}
              </div>
              <div className="text-green-600/40 text-xs font-mono">AMIRA</div>
            </div>
            <div>
              <div className="text-yellow-600/70 text-xs font-mono uppercase mb-1">Total Withdraw</div>
              <div className="text-yellow-400 font-mono font-bold">
                {userStakes && poolId < userStakes[0].length && poolId < userStakes[1].length ? 
                  (Number(formatEther(userStakes[0][poolId])) + Number(formatEther(userStakes[1][poolId]))).toFixed(6) : '0'}
              </div>
              <div className="text-yellow-600/40 text-xs font-mono">AMIRA</div>
            </div>
          </div>
          {userStakes && poolId < userStakes[2].length && userStakes[2][poolId] && (
            <div className="mt-3 text-center">
              <Badge variant="outline" className="border-green-700/50 text-green-500 font-mono">
                ✅ Already Withdrawn
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Status Description */}
      <div className="mb-4">
        <p className={`text-sm font-mono ${poolStatus.color}`}>
          {poolStatus.description}
        </p>
      </div>

      {/* Progress bar for running pools */}
      {poolStatus.status === 'running' && currentBlock && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-green-600/70 mb-2 font-mono">
            <span>Pool Progress</span>
            <span>
              {Math.round(((Number(currentBlock) - Number(startBlock)) / (Number(endBlock) - Number(startBlock))) * 100)}%
            </span>
          </div>
          <div className="w-full bg-green-900/20 rounded-full h-2 border border-green-700/30">
            <div 
              className="bg-gradient-to-r from-green-700 to-green-600 h-2 rounded-full transition-all shadow-lg shadow-green-700/30"
              style={{
                width: `${Math.min(100, Math.max(0, ((Number(currentBlock) - Number(startBlock)) / (Number(endBlock) - Number(startBlock))) * 100))}%`
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        {poolStatus.status === 'open' && (
          <>
            {address ? (
              <StakeModal poolId={poolId} rewardMultiplier={rewardMultiplierFormatted} />
            ) : (
              <Button 
                onClick={() => triggerWalletReminder("You need to connect your wallet to stake tokens! 🎯")}
                className="bg-green-800/30 border border-green-600/50 text-green-400 hover:bg-green-700/40 hover:text-green-300 font-mono text-sm"
              >
                🚀 Stake Tokens
              </Button>
            )}
          </>
        )}
        
        {poolStatus.status === 'finished' && (
          <>
            {address && hasStake ? (
              <WithdrawModal 
                poolId={poolId} 
                userStakeAmount={userStakeAmount}
                rewardMultiplier={rewardMultiplierFormatted}
              />
            ) : !address ? (
              <Button 
                onClick={() => triggerWalletReminder("Connect your wallet to check if you have tokens to withdraw! 💎")}
                className="bg-blue-800/30 border border-blue-600/50 text-blue-400 hover:bg-blue-700/40 hover:text-blue-300 font-mono text-sm"
              >
                💎 Withdraw All
              </Button>
            ) : (
              <Button disabled variant="outline" className="border-gray-700/50 text-gray-500 font-mono text-sm">
                💸 No Stake
              </Button>
            )}
          </>
        )}
        
        {poolStatus.status === 'running' && (
          <Button disabled variant="outline" className="border-gray-700/50 text-gray-500 font-mono text-sm">
            🔒 Pool Running
          </Button>
        )}
        
        {poolStatus.status === 'finished' && !hasStake && (
          <Button disabled variant="outline" className="border-gray-700/50 text-gray-500 font-mono text-sm">
            💸 No Stake
          </Button>
        )}
      </div>
    </div>
  )
}

// Stake Modal Component
function StakeModal({ poolId, rewardMultiplier }: { poolId: number, rewardMultiplier: string }) {
  const { address } = useAccount()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [open, setOpen] = useState(false)

  const { 
    data: approveHash, 
    writeContract: writeApprove, 
    isPending: isApprovePending 
  } = useWriteContract()

  const { 
    data: depositHash, 
    writeContract: writeDeposit, 
    isPending: isDepositPending 
  } = useWriteContract()

  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed } = 
    useWaitForTransactionReceipt({ hash: approveHash })

  const { isLoading: isDepositConfirming, isSuccess: isDepositConfirmed } = 
    useWaitForTransactionReceipt({ hash: depositHash })

  // Derive approval state from transaction status
  const isApproved = isApproveConfirmed

  const handleApprove = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    try {
      writeApprove({
        address: STAKE_TOKEN_ADDRESS,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [MULTISTAKING_CONTRACT_ADDRESS, parseEther(amount)],
      })
      toast.loading('Approving tokens...', { id: 'approve' })
    } catch (err) {
      console.error('Error approving tokens:', err)
      toast.error('Failed to approve tokens')
    }
  }

  const handleStake = async () => {
    if (!amount) {
      toast.error('Please enter amount')
      return
    }

    try {
      writeDeposit({
        address: MULTISTAKING_CONTRACT_ADDRESS,
        abi: MULTISTAKING_ABI,
        functionName: 'deposit',
        args: [BigInt(poolId), parseEther(amount)],
      })
      toast.loading('Staking tokens...', { id: 'deposit' })
    } catch (err) {
      console.error('Error depositing tokens:', err)
      toast.error('Failed to deposit tokens')
    }
  }

  // Handle approve confirmation
  useEffect(() => {
    if (isApproveConfirmed) {
      toast.success('Tokens approved! You can now stake.', { id: 'approve' })
    }
  }, [isApproveConfirmed])

  // Handle deposit confirmation
  useEffect(() => {
    if (isDepositConfirmed) {
      toast.success('Tokens staked successfully!', { id: 'deposit' })
      // Invalidate queries with scopeKey to refresh data
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key[0] === 'readContract' && 
                 (key[1]?.scopeKey === 'stakes' || key[1]?.scopeKey === 'pools')
        }
      })
      setOpen(false)
      setAmount('')
    }
  }, [isDepositConfirmed, queryClient])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-green-800/30 border border-green-600/50 text-green-400 hover:bg-green-700/40 hover:text-green-300 font-mono text-sm">
          🚀 Stake Tokens
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-green-700/50 text-green-400">
        <DialogHeader>
          <DialogTitle className="font-mono text-green-600">
            🎯 Stake in Pool #{poolId.toString().padStart(2, '0')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-green-900/10 border border-green-700/20 rounded-lg p-3">
            <div className="text-xs text-green-600/80 font-mono">
              Reward Multiplier: <span className="text-green-400 font-bold">{rewardMultiplier}x</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stake-amount" className="text-green-600 font-mono">Amount (AMIRA)</Label>
            <Input
              id="stake-amount"
              type="number"
              placeholder="e.g., 100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-black border-green-700/50 text-green-400 font-mono"
            />
          </div>

          <Separator className="bg-green-700/20" />

          <div className="space-y-3">
            <Button 
              onClick={handleApprove}
              disabled={isApprovePending || isApproveConfirming || !address || !amount || isApproved}
              className="w-full bg-green-800/30 border border-green-600/50 text-green-400 hover:bg-green-700/40 font-mono"
            >
              {isApprovePending || isApproveConfirming ? 'Approving...' : isApproved ? '✅ Approved' : '1. Approve Tokens'}
            </Button>
            
            <Button 
              onClick={handleStake}
              disabled={isDepositPending || isDepositConfirming || !address || !isApproved}
              className="w-full bg-blue-800/30 border border-blue-600/50 text-blue-400 hover:bg-blue-700/40 font-mono"
            >
              {isDepositPending || isDepositConfirming ? 'Staking...' : '2. Stake Tokens'}
            </Button>
          </div>

          {approveHash && <TransactionHash hash={approveHash} label="Approve" />}
          {depositHash && <TransactionHash hash={depositHash} label="Stake" />}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Withdraw Modal Component
function WithdrawModal({ 
  poolId, 
  userStakeAmount, 
  rewardMultiplier 
}: { 
  poolId: number
  userStakeAmount: string
  rewardMultiplier: string 
}) {
  const { address } = useAccount()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const { data: hash, writeContract, isPending } = useWriteContract()

  const { isLoading: isConfirming, isSuccess: isConfirmed } = 
    useWaitForTransactionReceipt({ hash })

  const handleWithdraw = async () => {
    try {
      writeContract({
        address: MULTISTAKING_CONTRACT_ADDRESS,
        abi: MULTISTAKING_ABI,
        functionName: 'withdraw',
        args: [BigInt(poolId)],
      })
      toast.loading('Withdrawing tokens...', { id: 'withdraw' })
    } catch (err) {
      console.error('Error withdrawing tokens:', err)
      toast.error('Failed to withdraw tokens')
    }
  }

  // Handle withdraw confirmation
  useEffect(() => {
    if (isConfirmed) {
      toast.success('Tokens withdrawn successfully!', { id: 'withdraw' })
      // Invalidate queries with scopeKey to refresh data
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key[0] === 'readContract' && 
                 (key[1]?.scopeKey === 'stakes' || key[1]?.scopeKey === 'pools')
        }
      })
      setOpen(false)
    }
  }, [isConfirmed, queryClient])

  const totalReward = (Number(userStakeAmount) * Number(rewardMultiplier)).toFixed(6)
  const bonusReward = (Number(totalReward) - Number(userStakeAmount)).toFixed(6)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-800/30 border border-blue-600/50 text-blue-400 hover:bg-blue-700/40 hover:text-blue-300 font-mono text-sm">
          💎 Withdraw
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-green-700/50 text-green-400">
        <DialogHeader>
          <DialogTitle className="font-mono text-green-600">
            💎 Withdraw from Pool #{poolId.toString().padStart(2, '0')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-blue-900/10 border border-blue-700/20 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-blue-600/70 text-xs font-mono uppercase">Principal</span>
                <p className="text-blue-400 font-mono font-bold">{userStakeAmount}</p>
                <p className="text-blue-500/60 text-xs font-mono">AMIRA</p>
              </div>
              <div>
                <span className="text-green-600/70 text-xs font-mono uppercase">Bonus</span>
                <p className="text-green-400 font-mono font-bold">+{bonusReward}</p>
                <p className="text-green-500/60 text-xs font-mono">AMIRA</p>
              </div>
              <div>
                <span className="text-yellow-600/70 text-xs font-mono uppercase">Total</span>
                <p className="text-yellow-400 font-mono font-bold">{totalReward}</p>
                <p className="text-yellow-500/60 text-xs font-mono">AMIRA</p>
              </div>
            </div>
          </div>

          <Button 
            onClick={handleWithdraw}
            disabled={isPending || isConfirming || !address}
            className="w-full bg-blue-800/30 border border-blue-600/50 text-blue-400 hover:bg-blue-700/40 font-mono"
          >
            {isPending || isConfirming ? 'Withdrawing...' : `💎 Withdraw ${totalReward} AMIRA`}
          </Button>

          {hash && <TransactionHash hash={hash} label="Withdraw" />}
        </div>
      </DialogContent>
    </Dialog>
  )
} 
 